import { useMemo } from 'react'
import { match } from 'ts-pattern'
import z from 'zod'
import { OutcomeIcon } from './outcome-icon.tsx'
import * as styles from './outcome-tooltip.css.ts'
import { type OutcomeType } from './outcome-types.ts'
import { getOutcomeLabel } from './outcome-utils.ts'

export const ErrorPopoverVariantSchema = z.enum(['short', 'long'])
export type ErrorPopoverVariant = z.infer<typeof ErrorPopoverVariantSchema>

export type OutcomeTooltipProps = {
  outcomeType: OutcomeType
  /** Selects between short (canned) and long (italic API message) Error variants. */
  errorVariant?: ErrorPopoverVariant
  /** Body copy for the long Error variant; ignored when errorVariant is 'short'. */
  errorMessage?: string
  /**
   * Opens the linked drawer. When provided, the popover renders a Details link for every outcome except the short
   * Error variant (which has no details to show); when omitted, no Details link renders.
   */
  onDetailsClick?: () => unknown
}

export const OutcomeTooltip: React.FC<OutcomeTooltipProps> = ({
  outcomeType,
  errorVariant = 'short',
  errorMessage,
  onDetailsClick,
}) => {
  const isErrorLong =
    outcomeType === 'error' && errorVariant === 'long' && errorMessage !== undefined && errorMessage.length > 0

  const popoverDescription = useMemo(
    () =>
      match(outcomeType)
        .with('true-positive', () => 'Pixee can validate the finding as a legitimate issue that warrants remediation')
        .with('likely-true-positive', () => (
          <>
            Pixee suspects a <strong>true</strong> positive, given the <strong>absence</strong> of typical false
            positive conditions or security controls
          </>
        ))
        .with('inconclusive', () => 'Pixee was not able to reach a firm conclusion')
        .with('potentially-exploitable', () => 'Pixee was not able to reach a firm conclusion')
        .with('no-fix-necessary', () => (
          <>
            Triage determined this finding to be negligible and does <strong>not</strong> warrant remediation
          </>
        ))
        .with('no-fix-necessary-false-positive', () => (
          <>
            Triage determined this finding to be a false positive and does <strong>not</strong> warrant remediation
          </>
        ))
        .with('no-fix-necessary-wont-fix', () => (
          <>
            Triage determined this finding to be negligible and does <strong>not</strong> warrant remediation
          </>
        ))
        .with('no-fix-necessary-not-exploitable', () => (
          <>
            Pixee determined this finding is <strong>not</strong> exploitable and does <strong>not</strong> warrant
            remediation
          </>
        ))
        .with(
          'error',
          () =>
            'A timeout or error occurred during analysis; specific reasoning and guidance will be provided as available per finding'
        )
        .with(
          'blocked',
          () => 'A manual review is required; specific reasoning and guidance will be provided as available per finding'
        )
        .with('excluded', () => "The finding's rule type has been configured as out-of-scope for this analysis")
        .with('exploitable', () => 'Pixee can validate the vulnerability as exploitable in your code')
        .exhaustive(),
    [outcomeType]
  )

  const description = isErrorLong && errorMessage !== undefined ? errorMessage : popoverDescription

  const isErrorShort = outcomeType === 'error' && !isErrorLong
  const showsDetailsLink = onDetailsClick !== undefined && !isErrorShort

  return (
    <div className={styles.tooltip}>
      <div className={styles.header}>
        <OutcomeIcon outcomeType={outcomeType} size="small" />
        <span className={styles.headerLabel}>{getOutcomeLabel(outcomeType)}</span>
      </div>
      <div className={styles.body}>
        <p className={isErrorLong ? styles.descriptionItalic : styles.description}>{description}</p>
        {showsDetailsLink && (
          <button type="button" className={styles.detailsLink} onClick={onDetailsClick}>
            Details
          </button>
        )}
      </div>
    </div>
  )
}
